import CheckBox from "@components/checkbox"
import TextInput from "@components/text_input"
import useTranslate from "@hooks/use_translate"
import {
  Add as AddIcon,
  Checklist as ChecklistIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  ImportExport as ImportExportIcon,
  Refresh as RefreshIcon
} from "@mui/icons-material"
import {
  Button,
  Collapse,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Tooltip
} from "@mui/material"
import {
  AiPromptUsage,
  FilterFormValues,
  GeminiModels,
  GPTModels
} from "@pages/main/scripts/prompts"
import {
  ALL_AI_ROLES,
  SHARED_ROLE_LABEL
} from "@pages/main/scripts/prompts/prompt.models"
import { useState } from "react"
import type { Control, UseFormReset } from "react-hook-form"

type SortMode = "NAME" | "VERSION_DATE"

interface PromptFiltersProps {
  control: Control<FilterFormValues>
  resetFilter: UseFormReset<FilterFormValues>
  sortMode: SortMode
  setSortMode: (mode: SortMode) => void
  onAddNew: () => void
  onRefetch: () => void
  bulkMode: boolean
  onToggleBulkMode: () => void
  onOpenImportExport: () => void
}

export const PromptFilters = ({
  control,
  resetFilter,
  sortMode,
  setSortMode,
  onAddNew,
  onRefetch,
  bulkMode,
  onToggleBulkMode,
  onOpenImportExport
}: PromptFiltersProps) => {
  const { translate } = useTranslate()
  const [showFilters, setShowFilters] = useState(false)

  return (
    <Stack direction="column" spacing={1} sx={{ p: 1 }}>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAddNew}
          sx={{ flex: 1, whiteSpace: "nowrap" }}
        >
          {translate("prompt_list_new")}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ImportExportIcon />}
          onClick={onOpenImportExport}
          sx={{ flex: 1, whiteSpace: "nowrap" }}
        >
          Import / Export
        </Button>
      </Stack>

      <Stack direction="row" spacing={0.5} alignItems="center">
        <TextInput
          type="text"
          name="search"
          control={control}
          size="small"
          placeholder={translate("prompt_list_search")}
        />
        <Tooltip title={translate("prompt_list_refresh")}>
          <IconButton onClick={onRefetch} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          color={showFilters ? "primary" : "inherit"}
          variant={showFilters ? "outlined" : "text"}
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{ flex: 1, whiteSpace: "nowrap" }}
        >
          {translate("prompt_list_filters")}
        </Button>
        <Tooltip title={translate("prompt_list_bulk_edit_hint")}>
          <Button
            size="small"
            color={bulkMode ? "primary" : "inherit"}
            variant={bulkMode ? "contained" : "text"}
            startIcon={bulkMode ? <CloseIcon /> : <ChecklistIcon />}
            onClick={onToggleBulkMode}
            sx={{ flex: 1, whiteSpace: "nowrap" }}
          >
            {translate(
              bulkMode ? "prompt_list_bulk_exit" : "prompt_list_bulk_edit"
            )}
          </Button>
        </Tooltip>
      </Stack>

      <Collapse in={showFilters}>
        <Stack
          direction="column"
          spacing={2}
          sx={{
            p: 2,
            bgcolor: "background.paper",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            mt: 1,
            boxShadow: 1
          }}
        >
          <Stack direction="row" spacing={1}>
            <TextInput
              type="select"
              label="Role"
              name="role"
              control={control}
              placeholder="All roles"
              selectOptions={[
                { value: "", label: "All" },
                { value: "SHARED", label: SHARED_ROLE_LABEL },
                ...ALL_AI_ROLES.map((v) => ({
                  value: v as string,
                  label: v as string
                }))
              ]}
            />
            <TextInput
              type="select"
              label="Usage"
              name="usage"
              control={control}
              placeholder="All Usages"
              selectOptions={[
                { value: "", label: "All" },
                ...Object.values(AiPromptUsage).map((v) => ({
                  value: v as string,
                  label: v as string
                }))
              ]}
            />
          </Stack>

          <TextInput
            type="select"
            label="AI Model"
            name="aiModelType"
            control={control}
            placeholder="All Models"
            groupBy={true}
            selectOptions={[
              {
                value: "",
                label: "All Models",
                icon: "🤖",
                group: "Quick Filters"
              },
              {
                value: "GPT",
                label: "All ChatGPT",
                icon: "🤖",
                group: "Quick Filters"
              },
              {
                value: "GEMINI",
                label: "All Gemini",
                icon: "💎",
                group: "Quick Filters"
              },
              ...Object.values(GPTModels).map((v) => ({
                value: v,
                label: v,
                icon: "🤖",
                group: "ChatGPT Models"
              })),
              ...Object.values(GeminiModels).map((v) => ({
                value: v,
                label: v,
                icon: "💎",
                group: "Gemini Models"
              }))
            ]}
          />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <CheckBox
              control={control}
              name="hasResponseSchema"
              label="Has Schema"
            />
            <Tooltip title="Clear Filters">
              <IconButton onClick={() => resetFilter()} size="small">
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Collapse>
      <RadioGroup
        row
        value={sortMode}
        onChange={(e) => setSortMode(e.target.value as SortMode)}
      >
        <FormControlLabel
          value="NAME"
          control={<Radio size="small" />}
          label={<span style={{ fontSize: "0.85rem" }}>Name</span>}
        />
        <FormControlLabel
          value="VERSION_DATE"
          control={<Radio size="small" />}
          label={<span style={{ fontSize: "0.85rem" }}>Version Date</span>}
        />
      </RadioGroup>
    </Stack>
  )
}
